import type { Job, WorkType } from "../types/job";

export interface JobFilters {
  search: string;
  workTypes: WorkType[];
  region: string;
  maxAgeHours: 24 | 168 | 240 | 336 | 720;
  minSalary: number;
}
export const DEFAULT_FILTERS: JobFilters = {
  search: "",
  workTypes: [],
  region: "all",
  maxAgeHours: 720,
  minSalary: 70_000,
};

export function dedupeJobs(jobs: Job[]) {
  const byApplicationUrl = new Map<string, Job>();
  jobs.forEach(job => {
    let key = job.applicationUrl.trim() || `job:${job.id}`;
    try {
      const url = new URL(key); url.hash = "";
      for (const name of [...url.searchParams.keys()]) if (/^(utm_.+|source|ref|referrer|gh_src|lever-source|lever-origin|src)$/i.test(name)) url.searchParams.delete(name);
      url.searchParams.sort(); key = url.toString().replace(/\/$/, "");
    } catch { /* Retain the source identity when a URL is unavailable. */ }
    const existing = byApplicationUrl.get(key);
    if (!existing) { byApplicationUrl.set(key, job); return; }
    if (job.radar && existing.radar) {
      const earliest = Date.parse(job.radar.firstSeen ?? "") < Date.parse(existing.radar.firstSeen ?? "") ? job.radar : existing.radar;
      const conservative = existing.radar.score <= job.radar.score ? existing.radar : job.radar;
      const tracked = existing.pipeline && existing.pipeline.stage !== "New" ? existing : job.pipeline && job.pipeline.stage !== "New" ? job : existing;
      byApplicationUrl.set(key, { ...tracked, radar: { ...conservative, actionable: existing.radar.actionable && job.radar.actionable, firstSeen: earliest.firstSeen, firstSeenAgeHours: earliest.firstSeenAgeHours, freshnessBand: earliest.freshnessBand, legacy: existing.radar.legacy || job.radar.legacy } });
    } else if (job.ageHours < existing.ageHours) byApplicationUrl.set(key, job);
  });
  return [...byApplicationUrl.values()];
}

export function jobRegion(job: Job) {
  return job.location.country ?? job.location.region ?? "Unknown";
}

export function filterJobs(jobs: Job[], filters: JobFilters) {
  const query = filters.search.trim().toLocaleLowerCase();
  return jobs.filter((job) => {
    if (job.postedAt && job.ageHours > Math.min(filters.maxAgeHours, 720)) return false;
    if (job.salary.cadMinimum !== null && job.salary.cadMinimum < filters.minSalary) return false;
    if (filters.workTypes.length > 0 && !filters.workTypes.includes(job.workType)) return false;
    if (filters.region !== "all" && jobRegion(job) !== filters.region) return false;
    if (query) {
      const haystack = [
        job.title,
        job.normalizedTitle,
        job.company,
        job.industry,
        job.location.city,
        job.location.region,
        job.location.country,
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase();
      if (!haystack.includes(query)) return false;
    }
    return true;
  });
}

export function getRegionOptions(jobs: Job[]) {
  return [...new Set(jobs.map(jobRegion))]
    .filter((region) => region !== "Worldwide" && region !== "Unknown")
    .sort();
}

export function formatSalary(job: Job, compact = false) {
  const { cadMinimum, cadMaximum } = job.salary;
  if (cadMinimum === null) return "Not disclosed · target $70K–$90K CAD";
  const displayFloor = 0;
  const displayCeiling = Number.POSITIVE_INFINITY;
  const formatter = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
    notation: compact ? "compact" : "standard",
  });
  const displayedMinimum = Math.min(Math.max(cadMinimum, displayFloor), displayCeiling);
  if (cadMaximum === null || cadMaximum === cadMinimum || displayedMinimum === displayCeiling) {
    return `${formatter.format(displayedMinimum)} CAD`;
  }
  const displayedMaximum = Math.min(Math.max(cadMaximum, displayedMinimum), displayCeiling);
  return `${formatter.format(displayedMinimum)}–${formatter.format(displayedMaximum)} CAD`;
}

export function formatLocation(job: Job) {
  const location = [job.location.city, job.location.region, job.location.country]
    .filter((value, index, values) => value && values.indexOf(value) === index)
    .join(", ");
  if (job.workType === "remote") {
    return `${location || "Global"} · Remote${job.eligibility ? ` (${job.eligibility})` : ""}`;
  }
  return location || "Location unavailable";
}

export function formatAge(ageHours: number) {
  if (ageHours < 1) return "just now";
  if (ageHours < 24) return `${Math.floor(ageHours)}h ago`;
  const days = Math.floor(ageHours / 24);
  return `${days}d ago`;
}

export interface SignalStyle {
  color: "green" | "yellow" | "purple";
  size: number;
  opacity: number;
  pulse: number;
  glow: number;
}

export function getSignalStyle(job: Job, index = 0): SignalStyle {
  const age = job.radar ? (job.radar.legacy ? 720 : job.radar.firstSeenAgeHours ?? 720) : (Number.isFinite(job.ageHours) ? job.ageHours : 720);
  const colors: SignalStyle["color"][] = ["green", "yellow", "purple"];
  const color = colors[index % colors.length];
  if (age <= 24) return { color, size: 1, opacity: 1, pulse: 0.46, glow: 1 };
  if (age <= 72) return { color, size: 0.82, opacity: 0.86, pulse: 0.3, glow: 0.75 };
  const decay = Math.max(0, 1 - (age - 72) / (720 - 72));
  return {
    color,
    size: 0.56 + decay * 0.14,
    opacity: 0.36 + decay * 0.34,
    pulse: 0.1 + decay * 0.1,
    glow: 0.26 + decay * 0.28,
  };
}
